import React, { useEffect, useRef } from 'react';
import { RemoteTrack, RemoteTrackPublication, Room, RoomEvent, Track, VideoQuality } from 'livekit-client';
import { useClerkToken } from 'src/hooks/useClerkToken';
import useLogger from 'src/hooks/useLogger';
import {
    type VideoPlaybackSnapshot,
    readVideoPlaybackSnapshot,
    videoPlaybackProgressed
} from 'src/lib/liveVideoPlayback';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEFAULT_TRACK = 'robot_overhead';
const TRACK_ORDER = ['robot_overhead', 'robot_perspective'];
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 10000;
const STALE_VIDEO_CHECK_INTERVAL_MS = 1000;
const STALE_VIDEO_TIMEOUT_MS = 6000;

type VideoFrameCallbackApi = Pick<
    HTMLVideoElement,
    'cancelVideoFrameCallback' | 'requestVideoFrameCallback'
>;

interface LiveKitViewerProviderProps {
    videoRef: React.RefObject<HTMLVideoElement>;
    robotId: string;
    roomId?: string | null;
    activeTrackName: string;
    onTracksChange?: (tracks: string[]) => void;
    onActiveTrackChange?: (trackName: string) => void;
}

interface LiveKitTokenResponse {
    url: string;
    token: string;
    room_id: string;
    identity: string;
}

interface LiveKitTokenRequest {
    robot_id: string;
    role: 'viewer';
    room_id?: string;
}

const fetchToken = async (
    robotId: string,
    roomId?: string | null,
    authToken?: string | null,
    traceId?: string
): Promise<LiveKitTokenResponse> => {
    const requestBody: LiveKitTokenRequest = {
        robot_id: robotId,
        role: 'viewer'
    };
    if (roomId) {
        requestBody.room_id = roomId;
    }

    const response = await fetch(`${API_BASE}/api/livekit/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            ...(traceId ? { 'X-Trace-Id': traceId } : {})
        },
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        throw new Error(`LiveKit token request failed: ${response.status}`);
    }

    return response.json();
};

const sortTracks = (tracks: string[]) =>
    [...new Set(tracks)].sort((a, b) => {
        const aIndex = TRACK_ORDER.indexOf(a);
        const bIndex = TRACK_ORDER.indexOf(b);
        if (aIndex === -1 && bIndex === -1) {
            return a.localeCompare(b);
        }
        if (aIndex === -1) {
            return 1;
        }
        if (bIndex === -1) {
            return -1;
        }
        return aIndex - bIndex;
    });

const getTrackName = (publication: RemoteTrackPublication) => {
    const currentTrackName = publication.trackName;
    if (currentTrackName) {
        return currentTrackName;
    }

    const legacyPublication = publication as { name?: string };
    const legacyTrackName = legacyPublication.name || '';
    return legacyTrackName;
};

const enforceVideoElementLayout = (video: HTMLVideoElement) => {
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    if (!video.style.objectFit) {
        video.style.objectFit = 'contain';
    }
};

const applyVideoSubscriptionPreference = (
    publication: RemoteTrackPublication,
    isActive: boolean
) => {
    publication.setSubscribed(isActive);
    if (isActive) {
        publication.setVideoQuality(VideoQuality.HIGH);
    }
};

const collectVideoTracks = (room: Room) => {
    const tracks: string[] = [];
    room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
            if (publication.kind !== Track.Kind.Video) {
                return;
            }
            const name = getTrackName(publication);
            if (name) {
                tracks.push(name);
            }
        });
    });
    return sortTracks(tracks);
};

export const LiveKitViewerProvider: React.FC<LiveKitViewerProviderProps> = ({
    videoRef,
    robotId,
    roomId,
    activeTrackName,
    onTracksChange,
    onActiveTrackChange
}) => {
    const { logError, logInfo, logWarn, traceId } = useLogger();
    const { getToken } = useClerkToken('dashboard-jwt');
    const activeTrackRef = useRef<RemoteTrack | null>(null);
    const logRef = useRef({ logError, logInfo, logWarn });
    const activeTrackNameRef = useRef(activeTrackName || DEFAULT_TRACK);
    const roomRef = useRef<Room | null>(null);
    const trackMapRef = useRef<Map<string, RemoteTrack>>(new Map());
    const tracksRef = useRef<string[]>([]);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectAttemptRef = useRef(0);
    const videoWatchdogIntervalRef = useRef<number | null>(null);
    const videoFrameCallbackIdRef = useRef<number | null>(null);
    const hasObservedPlaybackProgressRef = useRef(false);
    const lastPlaybackSnapshotRef = useRef<VideoPlaybackSnapshot | null>(null);
    const lastVideoProgressAtRef = useRef(0);
    const staleDisconnectInFlightRef = useRef(false);

    useEffect(() => {
        logRef.current = { logError, logInfo, logWarn };
    }, [logError, logInfo, logWarn]);

    useEffect(() => {
        activeTrackNameRef.current = activeTrackName || DEFAULT_TRACK;
        const room = roomRef.current;
        if (!room) {
            return;
        }

        const tracks = tracksRef.current;
        if (tracks.length && !tracks.includes(activeTrackNameRef.current)) {
            const fallback = tracks[0];
            if (fallback && fallback !== activeTrackNameRef.current) {
                activeTrackNameRef.current = fallback;
                onActiveTrackChange?.(fallback);
            }
        }

        const desired = activeTrackNameRef.current;
        room.remoteParticipants.forEach((participant) => {
            participant.trackPublications.forEach((publication) => {
                if (publication.kind !== Track.Kind.Video) {
                    return;
                }
                const name = getTrackName(publication);
                if (!name) {
                    return;
                }
                applyVideoSubscriptionPreference(publication, name === desired);
            });
        });

        const nextTrack = trackMapRef.current.get(activeTrackNameRef.current);
        if (nextTrack && videoRef.current) {
            if (activeTrackRef.current && activeTrackRef.current !== nextTrack) {
                activeTrackRef.current.detach(videoRef.current);
            }
            activeTrackRef.current = nextTrack;
            nextTrack.attach(videoRef.current);
            enforceVideoElementLayout(videoRef.current);
            videoRef.current.play().catch(() => undefined);
            lastPlaybackSnapshotRef.current = readVideoPlaybackSnapshot(videoRef.current);
            lastVideoProgressAtRef.current = performance.now();
            staleDisconnectInFlightRef.current = false;
        }
    }, [activeTrackName, onActiveTrackChange, videoRef]);

    useEffect(() => {
        if (!robotId) {
            return;
        }

        let canceled = false;
        const room = new Room();
        const trackMap = trackMapRef.current;
        roomRef.current = room;
        reconnectAttemptRef.current = 0;

        const clearVideoWatchdog = () => {
            if (videoWatchdogIntervalRef.current) {
                clearInterval(videoWatchdogIntervalRef.current);
                videoWatchdogIntervalRef.current = null;
            }
        };

        const clearVideoFrameCallback = (video: HTMLVideoElement | null) => {
            const callbackId = videoFrameCallbackIdRef.current;
            if (callbackId === null) {
                return;
            }
            const videoWithFrameApi = video as VideoFrameCallbackApi | null;
            if (!videoWithFrameApi?.cancelVideoFrameCallback) {
                videoFrameCallbackIdRef.current = null;
                return;
            }
            videoWithFrameApi.cancelVideoFrameCallback(callbackId);
            videoFrameCallbackIdRef.current = null;
        };

        const recordPlaybackSnapshot = () => {
            const video = videoRef.current;
            lastPlaybackSnapshotRef.current = video ? readVideoPlaybackSnapshot(video) : null;
        };

        const markVideoProgress = () => {
            recordPlaybackSnapshot();
            lastVideoProgressAtRef.current = performance.now();
            staleDisconnectInFlightRef.current = false;
        };

        const observeVideoProgress = () => {
            hasObservedPlaybackProgressRef.current = true;
            markVideoProgress();
        };

        const scheduleVideoFrameCallback = (video: HTMLVideoElement) => {
            const videoWithFrameApi = video as VideoFrameCallbackApi;
            const requestFrameCallback = videoWithFrameApi.requestVideoFrameCallback;
            if (!requestFrameCallback) {
                return;
            }
            const callbackId = requestFrameCallback.call(videoWithFrameApi, () => {
                const currentVideo = videoRef.current;
                if (!currentVideo) {
                    videoFrameCallbackIdRef.current = null;
                    return;
                }
                if (currentVideo !== video) {
                    videoFrameCallbackIdRef.current = null;
                    return;
                }
                if (!activeTrackRef.current) {
                    videoFrameCallbackIdRef.current = null;
                    return;
                }
                observeVideoProgress();
                scheduleVideoFrameCallback(video);
            });
            videoFrameCallbackIdRef.current = callbackId;
        };

        const clearPlaybackState = () => {
            clearVideoFrameCallback(videoRef.current);
            hasObservedPlaybackProgressRef.current = false;
            lastPlaybackSnapshotRef.current = null;
            lastVideoProgressAtRef.current = 0;
            staleDisconnectInFlightRef.current = false;
        };

        const detachActiveTrack = () => {
            if (activeTrackRef.current && videoRef.current) {
                activeTrackRef.current.detach(videoRef.current);
            }
            activeTrackRef.current = null;
            clearPlaybackState();
        };

        const clearReconnectTimeout = () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        const scheduleReconnect = () => {
            if (canceled || reconnectTimeoutRef.current) {
                return;
            }
            const delay = Math.min(
                RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttemptRef.current,
                RECONNECT_MAX_DELAY_MS
            );
            reconnectAttemptRef.current += 1;
            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectTimeoutRef.current = null;
                void connect();
            }, delay);
            logRef.current.logWarn('LiveKit reconnect scheduled', {
                event: 'livekit.reconnect.scheduled',
                fields: {
                    robot_id: robotId,
                    duration_ms: delay
                }
            });
        };

        const triggerStaleRecovery = (
            stalledForMs: number,
            playbackSnapshot: VideoPlaybackSnapshot
        ) => {
            if (canceled || staleDisconnectInFlightRef.current) {
                return;
            }
            staleDisconnectInFlightRef.current = true;
            logRef.current.logWarn('LiveKit video appears stale; forcing reconnect', {
                event: 'livekit.video.stale',
                fields: {
                    duration_ms: Math.round(stalledForMs),
                    media: {
                        current_time_seconds: playbackSnapshot.currentTimeSeconds,
                        paused: playbackSnapshot.paused,
                        ready_state: playbackSnapshot.readyState,
                        rendered_frame_count: playbackSnapshot.renderedFrameCount
                    },
                    robot_id: robotId
                }
            });
            detachActiveTrack();
            trackMap.clear();
            tracksRef.current = [];
            onTracksChange?.([]);
            scheduleReconnect();
            void room.disconnect();
        };

        const refreshTracks = () => {
            const tracks = collectVideoTracks(room);
            tracksRef.current = tracks;
            onTracksChange?.(tracks);

            if (!tracks.length) {
                return;
            }

            const desired = activeTrackNameRef.current || DEFAULT_TRACK;
            if (!tracks.includes(desired)) {
                const fallback = tracks[0];
                if (fallback && fallback !== desired) {
                    activeTrackNameRef.current = fallback;
                    onActiveTrackChange?.(fallback);
                }
            }

            const activeName = activeTrackNameRef.current || DEFAULT_TRACK;
            room.remoteParticipants.forEach((participant) => {
                participant.trackPublications.forEach((publication) => {
                    if (publication.kind !== Track.Kind.Video) {
                        return;
                    }
                    const name = getTrackName(publication);
                    if (!name) {
                        return;
                    }
                    applyVideoSubscriptionPreference(publication, name === activeName);
                });
            });
        };

        const handleTrackSubscribed = (track: RemoteTrack, publication: RemoteTrackPublication) => {
            if (track.kind !== Track.Kind.Video) {
                return;
            }

            const trackName = getTrackName(publication);
            if (!trackName) {
                logRef.current.logWarn('LiveKit video track has no name; skipping track attach.', {
                    event: 'livekit.track.unnamed',
                    fields: {
                        robot_id: robotId
                    }
                });
                return;
            }

            applyVideoSubscriptionPreference(
                publication,
                trackName === (activeTrackNameRef.current || DEFAULT_TRACK)
            );

            if (!videoRef.current) {
                logRef.current.logWarn('LiveKit video element not ready; skipping track attach.');
                return;
            }

            trackMap.set(trackName, track);

            if (trackName === (activeTrackNameRef.current || DEFAULT_TRACK)) {
                if (activeTrackRef.current && activeTrackRef.current !== track) {
                    detachActiveTrack();
                }

                activeTrackRef.current = track;
                track.attach(videoRef.current);
                enforceVideoElementLayout(videoRef.current);
                clearVideoFrameCallback(videoRef.current);
                scheduleVideoFrameCallback(videoRef.current);
                videoRef.current.play().catch(() => undefined);
                markVideoProgress();
                logRef.current.logInfo(`LiveKit track attached: ${trackName || 'video'}`);
            }

            refreshTracks();
        };

        const handleTrackUnsubscribed = (track: RemoteTrack) => {
            if (activeTrackRef.current === track) {
                detachActiveTrack();
            }
            for (const [name, mappedTrack] of trackMap.entries()) {
                if (mappedTrack === track) {
                    trackMap.delete(name);
                    break;
                }
            }
            refreshTracks();
        };

        const handleDisconnected = () => {
            detachActiveTrack();
            trackMap.clear();
            tracksRef.current = [];
            onTracksChange?.([]);
            logRef.current.logWarn('LiveKit disconnected', {
                event: 'livekit.disconnected',
                fields: {
                    robot_id: robotId
                }
            });
            scheduleReconnect();
        };

        const handleTrackSubscriptionFailed = (
            trackSid: string,
            error: unknown
        ) => {
            logRef.current.logWarn(`LiveKit track subscription failed: ${trackSid}`, {
                event: 'livekit.track.subscription_failed',
                fields: {
                    error: String(error),
                    robot_id: robotId
                }
            });
        };

        const startVideoWatchdog = () => {
            clearVideoWatchdog();
            markVideoProgress();
            videoWatchdogIntervalRef.current = window.setInterval(() => {
                if (canceled || !activeTrackRef.current) {
                    return;
                }
                const video = videoRef.current;
                if (!video) {
                    return;
                }
                const now = performance.now();
                const playbackSnapshot = readVideoPlaybackSnapshot(video);
                const previousSnapshot = lastPlaybackSnapshotRef.current;
                const playbackAdvanced =
                    previousSnapshot !== null &&
                    videoPlaybackProgressed(previousSnapshot, playbackSnapshot);
                lastPlaybackSnapshotRef.current = playbackSnapshot;
                if (playbackAdvanced) {
                    hasObservedPlaybackProgressRef.current = true;
                    lastVideoProgressAtRef.current = now;
                    staleDisconnectInFlightRef.current = false;
                    return;
                }
                if (!lastVideoProgressAtRef.current) {
                    markVideoProgress();
                    return;
                }
                if (!hasObservedPlaybackProgressRef.current) {
                    return;
                }
                if (
                    playbackSnapshot.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
                    playbackSnapshot.paused ||
                    playbackSnapshot.ended
                ) {
                    const stalledForMs = now - lastVideoProgressAtRef.current;
                    if (stalledForMs >= STALE_VIDEO_TIMEOUT_MS) {
                        triggerStaleRecovery(stalledForMs, playbackSnapshot);
                    }
                    return;
                }
                const stalledForMs = now - lastVideoProgressAtRef.current;
                if (stalledForMs >= STALE_VIDEO_TIMEOUT_MS) {
                    triggerStaleRecovery(stalledForMs, playbackSnapshot);
                }
            }, STALE_VIDEO_CHECK_INTERVAL_MS);
        };

        room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
        room.on(RoomEvent.TrackPublished, refreshTracks);
        room.on(RoomEvent.TrackUnpublished, refreshTracks);
        room.on(RoomEvent.Disconnected, handleDisconnected);
        room.on(RoomEvent.TrackSubscriptionFailed, handleTrackSubscriptionFailed);
        room.on(RoomEvent.ParticipantConnected, refreshTracks);
        room.on(RoomEvent.ParticipantDisconnected, refreshTracks);
        startVideoWatchdog();

        let connectInFlight = false;
        const connect = async () => {
            if (connectInFlight || canceled) {
                return;
            }
            connectInFlight = true;
            try {
                const authToken = await getToken();
                const { url, token, room_id } = await fetchToken(robotId, roomId, authToken, traceId);
                if (canceled) {
                    return;
                }
                logRef.current.logInfo(`Connecting to LiveKit room ${room_id}`);
                await room.connect(url, token, {
                    autoSubscribe: false
                });
                if (!canceled) {
                    refreshTracks();
                    reconnectAttemptRef.current = 0;
                    clearReconnectTimeout();
                }
            } catch (error) {
                if (!canceled) {
                    logRef.current.logError(`LiveKit connect failed: ${error}`, {
                        event: 'livekit.connect.failed',
                        fields: {
                            robot_id: robotId,
                            error: String(error)
                        }
                    });
                    scheduleReconnect();
                }
            } finally {
                connectInFlight = false;
            }
        };

        connect();

        return () => {
            canceled = true;
            detachActiveTrack();
            trackMap.clear();
            tracksRef.current = [];
            onTracksChange?.([]);
            clearVideoWatchdog();
            room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
            room.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
            room.off(RoomEvent.TrackPublished, refreshTracks);
            room.off(RoomEvent.TrackUnpublished, refreshTracks);
            room.off(RoomEvent.Disconnected, handleDisconnected);
            room.off(RoomEvent.TrackSubscriptionFailed, handleTrackSubscriptionFailed);
            room.off(RoomEvent.ParticipantConnected, refreshTracks);
            room.off(RoomEvent.ParticipantDisconnected, refreshTracks);
            clearReconnectTimeout();
            room.disconnect();
        };
    }, [robotId, roomId, videoRef, onTracksChange, onActiveTrackChange, getToken, traceId]);

    return null;
};
